import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTotalDowntimeKPI } from "./hooks/useTotalDowntimeKPI";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileChartContent,
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import TotalDowntimeKPISettings from "./TotalDowntimeKPISettings";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

const SUMMARY_STAT_VALUE_MAX_PX = 18; // was text-lg
const SUMMARY_STAT_VALUE_MIN_PX = 12;

// Own component (not inline in SummaryStats) so useAutoFitFontSize is unaffected by SummaryStats'
// own early `if (!summaryData) return null` guard — see Rules of Hooks.
const SummaryStatValue = ({ text }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: SUMMARY_STAT_VALUE_MAX_PX,
    minPx: SUMMARY_STAT_VALUE_MIN_PX,
  });

  return (
    <div ref={ref} className="max-w-full truncate font-bold text-primary-600" style={{ fontSize: `${fontSizePx}px` }}>
      {text}
    </div>
  );
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

let _hatchPattern = null;
function getHatchPattern() {
  if (_hatchPattern) return _hatchPattern;
  if (typeof document === "undefined") return null;
  const size = 8;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "rgba(229, 231, 235, 0.5)";
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = "rgba(107, 114, 128, 0.8)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, size);
  ctx.lineTo(size, 0);
  ctx.stroke();
  _hatchPattern = ctx.createPattern(canvas, "repeat");
  return _hatchPattern;
}

// Summary Stats Component
const SummaryStats = ({ summaryData }) => {
  if (!summaryData) return null;

  const fmt = (n, decimals = 1) => {
    try {
      return Number(n || 0).toFixed(decimals);
    } catch {
      return String(n);
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">
          Total Downtime
        </div>
        <SummaryStatValue text={`${fmt(summaryData.total_downtime_hours, 2)} hrs`} />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">
          Avg Daily Downtime
        </div>
        <SummaryStatValue
          text={
            summaryData.avg_daily_downtime_minutes >= 60
              ? `${fmt(summaryData.avg_daily_downtime_minutes / 60, 2)} hrs`
              : `${fmt(summaryData.avg_daily_downtime_minutes)} min`
          }
        />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">
          Overall Downtime
        </div>
        <SummaryStatValue text={`${fmt(summaryData.overall_downtime_percentage)}%`} />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
        <div className="text-xs text-gray-600 font-medium">Days Analyzed</div>
        <SummaryStatValue text={String(summaryData.days_analyzed)} />
      </div>
    </div>
  );
};

function TotalDowntimeKPI({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    // Data
    chartData,
    summaryData,
    loading,
    error,

    // Metadata
    objectTypes,
    objectTypeActivities,
    availableResources,
    isMetadataLoaded,

    // Configuration state
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    selectedResources,
    dateRange,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,

    // Setters
    setTitle,
    setSelectedObjectType,
    setCycleStartActivity,
    setCycleEndActivity,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,

    // Setters (resources + date)
    setSelectedResources,
    setDateRange,

    // Actions
    fetchMetadata,
    fetchDowntimeData,
  } = useTotalDowntimeKPI(activeFile);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.selectedObjectType !== undefined) setSelectedObjectType(s.selectedObjectType);
    if (s.cycleStartActivity !== undefined) setCycleStartActivity(s.cycleStartActivity);
    if (s.cycleEndActivity !== undefined) setCycleEndActivity(s.cycleEndActivity);
    if (s.selectedResources !== undefined) setSelectedResources(s.selectedResources);
    if (s.dateRange !== undefined) setDateRange(s.dateRange);
    if (s.shiftStart !== undefined) setShiftStart(s.shiftStart);
    if (s.shiftEnd !== undefined) setShiftEnd(s.shiftEnd);
    if (s.breaks !== undefined) setBreaks(s.breaks);
    if (s.toleranceThreshold !== undefined) setToleranceThreshold(s.toleranceThreshold);
    if (s.ignoreShortTransitions !== undefined) setIgnoreShortTransitions(s.ignoreShortTransitions);
    if (s.includeNonWorkingDays !== undefined) setIncludeNonWorkingDays(s.includeNonWorkingDays);
    if (s.includeNoDataResources !== undefined) setIncludeNoDataResources(s.includeNoDataResources);
    if (s.multiResourceMode !== undefined) setMultiResourceMode(s.multiResourceMode);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, cycleStartActivity, cycleEndActivity,
      selectedResources, dateRange, shiftStart, shiftEnd, breaks,
      toleranceThreshold, ignoreShortTransitions, includeNonWorkingDays,
      includeNoDataResources, multiResourceMode,
    }));
  }, [title, selectedObjectType, cycleStartActivity, cycleEndActivity,
    selectedResources, dateRange, shiftStart, shiftEnd, breaks,
    toleranceThreshold, ignoreShortTransitions, includeNonWorkingDays,
    includeNoDataResources, multiResourceMode]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Total Downtime");
  }, [title]);

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (selectedResources.length > 0 && selectedObjectType) {
      fetchDowntimeData();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
    fetchMetadata();
  }, [fetchMetadata]);

  const handleApplySettings = useCallback((values) => {
    setTitle(values.title);
    setSelectedObjectType(values.selectedObjectType);
    setCycleStartActivity(values.cycleStartActivity);
    setCycleEndActivity(values.cycleEndActivity);
    setSelectedResources(values.selectedResources);
    setDateRange(values.dateRange);
    setShiftStart(values.shiftStart);
    setShiftEnd(values.shiftEnd);
    setBreaks(values.breaks);
    setToleranceThreshold(values.toleranceThreshold);
    setIgnoreShortTransitions(values.ignoreShortTransitions);
    setIncludeNonWorkingDays(values.includeNonWorkingDays);
    setIncludeNoDataResources(values.includeNoDataResources);
    setMultiResourceMode(values.multiResourceMode);
    if (onTitleChange) onTitleChange(values.title);
    // Pass values explicitly to avoid stale state
    fetchDowntimeData(values);
  }, [
    setTitle,
    setSelectedObjectType,
    setCycleStartActivity,
    setCycleEndActivity,
    setSelectedResources,
    setDateRange,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    onTitleChange,
    fetchDowntimeData,
  ]);

  // Provide refresh function to parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(() => {
        if (isMetadataLoaded && selectedResources.length > 0) {
          fetchDowntimeData();
        }
      });
    }
  }, [onRefresh, isMetadataLoaded, selectedResources, fetchDowntimeData]);

  // Header actions - settings gear button (passed to GridComponent header)
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings],
  );

  // Provide header actions to parent (GridComponent)
  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  // Determine whether to display Y-axis in hours or minutes
  const useHours = useMemo(() => {
    if (!chartData || chartData.length === 0) return false;
    const maxMinutes = Math.max(
      ...chartData.filter((d) => !d.no_data).map((d) => d.downtime_minutes),
      0,
    );
    return maxMinutes >= 60;
  }, [chartData]);

  const BLUE_BG = "rgba(59, 130, 246, 0.7)";
  const BLUE_BORDER = "rgba(59, 130, 246, 1)";
  const GRAY_BG = "rgba(209, 213, 219, 0.7)";
  const GRAY_BORDER = "rgba(209, 213, 219, 1)";

  const hasNoDataBars = chartData && chartData.some((d) => d.no_data);

  const chartjsData = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const maxReal = Math.max(
      ...chartData
        .filter((d) => !d.no_data)
        .map((d) => (useHours ? d.downtime_hours : d.downtime_minutes)),
      0,
    );
    const fullHeight = Math.max(maxReal, 1);

    const labels = [], values = [], backgrounds = [], borders = [], noDataFlags = [];
    const hatchPattern = getHatchPattern();

    chartData.forEach((d) => {
      labels.push(
        new Date(d.date).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }),
      );
      if (d.no_data) {
        values.push(fullHeight);
        backgrounds.push(hatchPattern || GRAY_BG);
        borders.push(GRAY_BORDER);
        noDataFlags.push(true);
      } else {
        values.push(useHours ? d.downtime_hours : d.downtime_minutes);
        backgrounds.push(BLUE_BG);
        borders.push(BLUE_BORDER);
        noDataFlags.push(false);
      }
    });

    return {
      labels,
      datasets: [{
        label: t('tiles:downtime.legend'),
        data: values,
        backgroundColor: backgrounds,
        borderColor: borders,
        borderWidth: 1,
        borderRadius: (ctx) => noDataFlags[ctx.dataIndex] ? 0 : 6,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      }],
    };
  }, [chartData, useHours]);

  const chartjsOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: hasNoDataBars,
        position: "top",
        labels: {
          generateLabels: () => [
            { text: t('tiles:downtime.legend'), fillStyle: BLUE_BG, strokeStyle: BLUE_BORDER, lineWidth: 1 },
            { text: "No production data", fillStyle: getHatchPattern() || GRAY_BG, strokeStyle: GRAY_BORDER, lineWidth: 1 },
          ],
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const d = chartData[context.dataIndex];
            if (!d) return "";
            const fmt = (n, dec = 1) => Number(n || 0).toFixed(dec);
            if (d.no_data) return "No production data";
            const displayValue = useHours ? d.downtime_hours : d.downtime_minutes;
            const unit = useHours ? "hrs" : "min";
            return `Downtime: ${fmt(displayValue, useHours ? 2 : 1)} ${unit} (${fmt(d.downtime_percentage)}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t('common:chart.date'), color: "#4b5563", font: { size: 12 } },
        ticks: { maxRotation: 45, color: "#6b7280", font: { size: 11 } },
      },
      y: {
        title: {
          display: true,
          text: useHours ? "Downtime (Hours)" : "Downtime (Minutes)",
          color: "#4b5563",
          font: { size: 12 },
        },
        ticks: { color: "#6b7280", font: { size: 11 } },
        beginAtZero: true,
      },
    },
  }), [hasNoDataBars, useHours, chartData]);

  const hasFetched = chartData.length > 0 || summaryData !== null;
  const showChart =
    isMetadataLoaded && !loading && !error && chartData.length > 0;
  const showNoData =
    isMetadataLoaded &&
    !loading &&
    !error &&
    chartData.length === 0 &&
    summaryData !== null;
  const showSelectionPrompt =
    !loading && !error && summaryData === null && chartData.length === 0;

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="lg" grow>
        {showChart && (
          <>
            <SummaryStats summaryData={summaryData} />
            <TileChartContent height={440} className="flex-grow">
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                <Bar data={chartjsData} options={chartjsOptions} />
              </div>
            </TileChartContent>
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={
              typeof error === "string"
                ? error
                : "An unexpected error occurred while loading data."
            }
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded && selectedResources.length > 0) {
                      fetchDowntimeData();
                    } else {
                      fetchMetadata();
                    }
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors duration-200"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}

        {showNoData && (
          <TileStatusContent
            status="empty"
            title="No Data Available"
            message="No downtime data found for the selected resources and configuration. Try adjusting your date range or resource selection."
            icon={
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />
        )}

        {showSelectionPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Downtime Analysis"
            message="Configure KPI to select resources and configure shift parameters, then calculate downtime."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Configure KPI
              </button>
            }
            icon={
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                />
              </svg>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Total Downtime KPI Settings"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          cycleStartActivity,
          cycleEndActivity,
          selectedResources: [...selectedResources],
          dateRange: { ...dateRange },
          shiftStart,
          shiftEnd,
          breaks: breaks.map((b) => ({ ...b })),
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            values.selectedResources?.length > 0 &&
            values.shiftStart &&
            values.shiftEnd &&
            !!values.selectedObjectType
          ) || loading
        }
        applyButtonText={
          loading
            ? "Calculating..."
            : hasFetched
              ? "Update Chart"
              : "Calculate Downtime"
        }
      >
        {({ values, setField, update }) => (
          <TotalDowntimeKPISettings
            title={values.title}
            objectTypes={objectTypes}
            objectTypeActivities={objectTypeActivities}
            selectedObjectType={values.selectedObjectType}
            cycleStartActivity={values.cycleStartActivity}
            cycleEndActivity={values.cycleEndActivity}
            availableResources={availableResources}
            selectedResources={values.selectedResources}
            dateRange={values.dateRange}
            shiftStart={values.shiftStart}
            shiftEnd={values.shiftEnd}
            breaks={values.breaks}
            toleranceThreshold={values.toleranceThreshold}
            ignoreShortTransitions={values.ignoreShortTransitions}
            includeNonWorkingDays={values.includeNonWorkingDays}
            includeNoDataResources={values.includeNoDataResources}
            multiResourceMode={values.multiResourceMode}
            onTitleChange={(v) => setField("title", v)}
            onObjectTypeChange={(v) => setField("selectedObjectType", v)}
            onCycleStartActivityChange={(v) =>
              setField("cycleStartActivity", v)
            }
            onCycleEndActivityChange={(v) => setField("cycleEndActivity", v)}
            onResourceSelectionChange={(v) => setField("selectedResources", v)}
            onDateRangeChange={(v) => setField("dateRange", v)}
            onShiftStartChange={(v) => setField("shiftStart", v)}
            onShiftEndChange={(v) => setField("shiftEnd", v)}
            onAddBreak={() =>
              update((p) => ({
                ...p,
                breaks: [...p.breaks, { start: "12:00", end: "12:30" }],
              }))
            }
            onRemoveBreak={(idx) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.filter((_, i) => i !== idx),
              }))
            }
            onUpdateBreak={(idx, field, val) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.map((b, i) =>
                  i === idx ? { ...b, [field]: val } : b,
                ),
              }))
            }
            onToleranceThresholdChange={(v) =>
              setField("toleranceThreshold", v)
            }
            onIgnoreShortTransitionsChange={(v) =>
              setField("ignoreShortTransitions", v)
            }
            onIncludeNonWorkingDaysChange={(v) =>
              setField("includeNonWorkingDays", v)
            }
            onIncludeNoDataResourcesChange={(v) =>
              setField("includeNoDataResources", v)
            }
            onMultiResourceModeChange={(v) => setField("multiResourceMode", v)}
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default TotalDowntimeKPI;
